// services/referralsApi.ts — HTTP client for Phase 3 referrals + account
// deletion endpoints.
//
// All authed endpoints go through authedFetch (handles bearer + 401 refresh).
// The public leaderboard is plain fetch — no auth required and we don't want
// a stale token to introduce a 401 → refresh round trip on a marketing page.

import { authedFetch, API_BASE } from "../auth/tokens";

export interface ReferralDashboardActiveReferee {
  firstName: string;
  attributedAt: string;
}

export interface ReferralDashboardByBook {
  slug: string;
  title: string;
  signups: number;
  subscribed: number;
}

export interface ReferralDashboardTier {
  used: number;
  max: number;
  ratePerReferee: number;
}

export interface ReferralDashboard {
  totalTurnsEarnedLifetime: number;
  activeReferees: ReferralDashboardActiveReferee[];
  pendingReferees: number;
  currentMonthlyBonusRate: number;
  tierProgress: {
    tier1: ReferralDashboardTier;
    tier2: ReferralDashboardTier;
    tier3: ReferralDashboardTier;
    tier4: { used: number; ratePerReferee: number };
    capProgress: number;
  };
  byBook: ReferralDashboardByBook[];
  frozen: boolean;
  flaggedCount: number;
}

export interface AccountDeletionStatus {
  pending: boolean;
  scheduledAt: string | null;
}

export interface LeaderboardEntry {
  firstName: string;
  lastInitial: string;
  lifetimeBonusTurns: number;
  readersBrought: number;
}

export interface LeaderboardPayload {
  topReferrers: LeaderboardEntry[];
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // non-JSON
    }
  }
  if (!res.ok) {
    const errBody = (body as { error?: string }) || {};
    throw new Error(
      errBody.error || (text ? text.slice(0, 200) : `Status ${res.status}`),
    );
  }
  return body as T;
}

export async function getReferralDashboard(): Promise<ReferralDashboard> {
  const res = await authedFetch(`/api/referrals/dashboard`, { method: "GET" });
  return parseOrThrow<ReferralDashboard>(res);
}

/**
 * Schedule account deletion. The 14-day cooldown starts now. Idempotent —
 * a follow-up call against a user with a pending row returns the same
 * scheduledAt and `newlyScheduled: false`.
 */
export async function scheduleAccountDeletion(): Promise<{
  ok: true;
  scheduledAt: string;
  newlyScheduled: boolean;
}> {
  const res = await authedFetch(`/api/account`, { method: "DELETE" });
  return parseOrThrow<{ ok: true; scheduledAt: string; newlyScheduled: boolean }>(
    res,
  );
}

export async function cancelAccountDeletion(): Promise<{ ok: true }> {
  const res = await authedFetch(`/api/account/cancel-deletion`, {
    method: "POST",
  });
  return parseOrThrow<{ ok: true }>(res);
}

export async function getAccountDeletionStatus(): Promise<AccountDeletionStatus> {
  const res = await authedFetch(`/api/account/deletion-status`, {
    method: "GET",
  });
  return parseOrThrow<AccountDeletionStatus>(res);
}

/**
 * Public leaderboard — no auth, cached 5min server-side. The fetch goes
 * via window.fetch directly so a stale token doesn't trigger a refresh
 * round-trip when the user isn't even signed in.
 */
export async function getPublicLeaderboard(): Promise<LeaderboardPayload> {
  const res = await fetch(`${API_BASE}/api/public/leaderboard`, {
    method: "GET",
  });
  return parseOrThrow<LeaderboardPayload>(res);
}
